import { RcdCard } from './RcdCard';
import { DbCard } from './DbCard';
import { StandardCard } from './StandardCard';
import { WorkshopCard } from './WorkshopCard';
import { DualCheckCard } from './DualCheckCard';
import MobileTestCardRcd from './MobileTestCardRcd';
import MobileTestCardDistributionBoard from './MobileTestCardDistributionBoard';
import MobileTestCardDualCheck from './MobileTestCardDualCheck';
import DefaultTestCard from './DefaultTestCard';
import { assetDisplayLabel, buildingRoomDisplay, cleanText } from '../../../../lib/formatUtils';
import Badge from '../../../../components/ui/Badge';

/**
 * Dispatcher: renders the correct card type (RCD, Distribution Board, or Standard)
 * based on asset.asset_type and session context. Shared layout (code, location, status)
 * is rendered here; type-specific actions are delegated to the sub-cards.
 */
export function AssetCard({
  asset,
  sessionEntry,
  sessionAssetType,
  showTypeBadge,
  typeLabel,
  formatDisplayShort,
  isSyncing,
  sessionTestType,
  sessionTestDate,
  isExitEmergency,
  rcdInput,
  rcdEntered = false,
  setRcdInput,
  dualCheckInput,
  setDualCheckInput,
  handleQuickPass,
  openEditModal,
  openFailEditModal,
  isQuickPassAllowed,
  getQuickPassTooltip,
}) {
  const assetTypeLower = (asset.asset_type || '').toLowerCase();
  const isDb = sessionAssetType === 'combined_db_rcd' && assetTypeLower === 'distribution_board';
  const isRcd = sessionAssetType === 'combined_db_rcd' && assetTypeLower === 'rcd';
  const isWorkshop = assetTypeLower === 'workshop_equipment';
  // ceiling_fan / wall_heater / automated_gate now record a SINGLE Operational
  // Check (one Pass/Fail + fail popup), so they route to the Standard card like
  // any other single-test type. The dual-check path below is retired — no asset
  // type routes to it — but is left in place until the dead dual-check
  // components are removed in a later cleanup.
  const isDualCheck = false;
  const isStandard = !isDb && !isRcd && !isWorkshop && !isDualCheck;
  const tested = sessionEntry != null;
  const isParentRow = isDb;

  // RCD, DB, Standard and dual-check assets render self-contained mobile cards
  // that carry their own header; only the remaining types use the shared one.
  const hasSelfContainedMobileCard = isDb || isRcd || isStandard || isDualCheck;

  /* Narrow badge column (85px) for RCD/DB session; full width (140px) for Exit Lights etc. */
  const isBadgeSlim = sessionAssetType === 'combined_db_rcd';
  const badgeDisplayLabel =
    assetTypeLower === 'distribution_board' || typeLabel === 'Distribution Board' ? 'Dist. Bd' : typeLabel;

  const code = assetDisplayLabel(asset);
  const loc = buildingRoomDisplay(asset.building, asset.room);
  const make = cleanText(asset.manufacturer ?? asset.make);
  const model = cleanText(asset.model);
  const capacity = cleanText(asset.extra_details?.capacity ?? asset.rating);

  // RCD mobile card fields — amperage formatted the same way as the legacy RcdCard.
  const rcdAmperage = asset.extra_details?.capacity ?? asset.rating ?? asset.extra_details?.amperage;
  const rcdRating = rcdAmperage != null && rcdAmperage !== ''
    ? (String(rcdAmperage).match(/^\d+(\.\d+)?$/) ? `${rcdAmperage}A` : String(rcdAmperage))
    : '';
  const rcdIsSynced = sessionEntry?.synced ?? false;
  const rcdSavedResult = sessionEntry?.status ?? 'pass';
  // Mobile card defaults to unselected: only reflect a push result once the
  // tech has actually touched this RCD's input (rcdEntered) or it's saved.
  const rcdPushResult = tested
    ? rcdSavedResult
    : (rcdEntered ? (rcdInput?.pushButton ?? null) : null);
  const rcdTripTimeMs = tested
    ? (sessionEntry?.tripTimeMs ?? rcdInput?.tripTimeMs ?? '')
    : (rcdInput?.tripTimeMs ?? '');

  // DB mobile card: result is derived from the saved entry only (Pass saves
  // immediately, Fail via the defect modal), unselected until recorded.
  const dbIsSynced = sessionEntry?.synced ?? false;
  const dbResult = tested ? (sessionEntry?.status ?? 'pass') : null;

  // Standard mobile card: same immediate-pass/fail-modal pattern, but Pass is
  // only immediate when isQuickPassAllowed (e.g. blocked for Operating Time,
  // which must capture trip_time_ms via the modal — see sessionGroupingUtils).
  const standardIsSynced = sessionEntry?.synced ?? false;
  const standardResult = tested ? (sessionEntry?.status ?? 'pass') : null;
  const standardQuickPassOk = isStandard ? isQuickPassAllowed(asset, sessionTestType, sessionAssetType) : true;

  // Dual-check mobile card: Visual is the primary (saved) result; Operational is
  // held in local input. Both stay unselected until the tech answers them.
  const dualCheckIsSynced = sessionEntry?.synced ?? false;
  const dualVisualResult = tested ? (sessionEntry?.status ?? null) : (dualCheckInput?.visual ?? null);
  const dualOperationalResult = dualCheckInput?.operational ?? null;

  const qty = asset.quantity ?? asset.extra_details?.quantity;
  const quantityDisplay = qty != null && Number(qty) !== 1 ? `Qty: ${qty}` : null;
  const detailParts = [make, model, capacity, quantityDisplay].filter(Boolean);
  const makeModelLine = detailParts.length ? detailParts.join(' / ') : null;

  // Compliance status pill → kit <Badge> (was bespoke .status-badge .status-*).
  const testStatus = asset.test_status || 'current';
  const complianceVariant = testStatus === 'overdue' ? 'danger'
    : testStatus === 'due_soon' ? 'warning'
      : 'success';
  const complianceLabel = asset.test_status
    ? asset.test_status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : '-';

  const liClasses = [
    'asset-card',
    tested ? 'asset-card-tested' : '',
    isParentRow ? 'asset-card-parent' : '',
    isRcd ? 'asset-card-rcd' : '',
    isDualCheck ? 'asset-card-rcd' : '',
    isStandard ? 'asset-card-standard' : '',
  ].filter(Boolean).join(' ');

  return (
    <li key={asset.id} className={liClasses}>
      <div className="asset-card-main">
        {/* Mobile: Header — row 1 = ID (left) + location (right), row 2 = make/model;
            hidden for RCD, DB, and Standard (they render self-contained mobile cards). */}
        {!hasSelfContainedMobileCard && (
          <div className="asset-card-mobile-header">
            <div className="asset-card-mobile-header-row1">
              {!code.startsWith('#') ? (
                <span className="asset-card-mobile-id">{code}</span>
              ) : (
                <span className="asset-card-mobile-id">
                  <span className="asset-card-mobile-id-hash">#</span>{code.slice(1)}
                </span>
              )}
              <span className="asset-card-mobile-loc">
                {loc !== '-' ? loc : 'Location not set'}
              </span>
            </div>
            <span className="asset-card-mobile-make-model">
              {makeModelLine ?? 'No make / model on file'}
            </span>
          </div>
        )}
        {/* Desktop: table row — render for ALL asset types including RCD (Code, Badge, Last Test, Status, Make/Model) */}
        <div className={`asset-card-fields asset-card-fields-desktop ${isBadgeSlim ? 'asset-card-fields-badge-slim' : ''}`.trim()}>
          <span className="asset-card-code">{code}</span>
          <span className="asset-card-badge-col">
            {showTypeBadge ? (
              <span className="badge badge-gray asset-card-type">{badgeDisplayLabel}</span>
            ) : null}
          </span>
          <span className="asset-card-meta-col">
            Last: {asset.last_test_date ? formatDisplayShort(asset.last_test_date) : 'Never'}
          </span>
          <span className="asset-card-status-col">
            <Badge variant={complianceVariant}>{complianceLabel}</Badge>
          </span>
          <div className="asset-card-location-stack">
            {loc !== '-' && (
              <span className="asset-card-loc-desktop" title={loc}>{loc}</span>
            )}
            <span className="asset-card-make-model" title={makeModelLine || '-'}>{makeModelLine || '-'}</span>
          </div>
        </div>
        {/* Mobile: collapsible Details for Workshop (not used for Standard — StandardCard gets
            detailsSlot; DB now renders a self-contained mobile card). */}
        {!hasSelfContainedMobileCard && (
          <details className="asset-card-mobile-details">
            <summary className="asset-card-mobile-details-summary">Details</summary>
            <div className="asset-card-mobile-details-inner">
              <span className="asset-card-mobile-detail-row">
                Last: {asset.last_test_date ? formatDisplayShort(asset.last_test_date) : 'Never'}
              </span>
              <span className="asset-card-mobile-detail-row">
                Status: <Badge variant={complianceVariant}>{complianceLabel}</Badge>
              </span>
              {showTypeBadge && (
                <span className="asset-card-mobile-detail-row">
                  <span className="badge badge-gray asset-card-type">{badgeDisplayLabel}</span>
                </span>
              )}
              {makeModelLine && (
                <span className="asset-card-mobile-detail-row asset-card-make-model">{makeModelLine}</span>
              )}
            </div>
          </details>
        )}

        {isDb && (
          <>
            <div className="asset-card-desktop-only">
              <DbCard
                asset={asset}
                sessionEntry={sessionEntry}
                onQuickPass={(a) => handleQuickPass(a, 'Visual Check', sessionTestDate)}
                onFailEdit={(a) => openFailEditModal(a, { testType: 'Visual Check' })}
                onEdit={openEditModal}
                isSyncing={isSyncing}
              />
            </div>
            <div className="asset-card-mobile-only">
              <MobileTestCardDistributionBoard
                boardCode={code}
                assetType={typeLabel || 'Distribution Board'}
                make={make}
                model={model}
                location={loc !== '-' ? loc : undefined}
                result={dbResult}
                onResultChange={(next) => {
                  if (next === 'pass') handleQuickPass(asset, 'Visual Check', sessionTestDate);
                  else if (next === 'fail') openFailEditModal(asset, { testType: 'Visual Check' });
                }}
                tested={tested}
                isSynced={dbIsSynced}
                onEdit={() => openEditModal(asset, sessionEntry)}
              />
            </div>
          </>
        )}

        {isRcd && (
          <>
            <div className="asset-card-desktop-only">
              <RcdCard
                asset={asset}
                sessionEntry={sessionEntry}
                rcdInput={rcdInput}
                onSetInput={setRcdInput}
                onEdit={openEditModal}
              />
            </div>
            <div className="asset-card-mobile-only">
              <MobileTestCardRcd
                circuitLabel={code}
                make={make}
                model={model}
                rating={rcdRating}
              pushResult={rcdPushResult}
              onPushResultChange={(next) => setRcdInput(asset.id, { pushButton: next })}
              tripTimeMs={rcdTripTimeMs}
              onTripTimeChange={(next) => setRcdInput(asset.id, { tripTimeMs: next })}
              tested={tested}
                isSynced={rcdIsSynced}
                onEdit={() => openEditModal(asset, sessionEntry)}
              />
            </div>
          </>
        )}

        {isDualCheck && (
          <>
            <div className="asset-card-desktop-only">
              <DualCheckCard
                asset={asset}
                sessionEntry={sessionEntry}
                dualCheckInput={dualCheckInput}
                onSetInput={setDualCheckInput}
                onEdit={openEditModal}
              />
            </div>
            <div className="asset-card-mobile-only">
              <MobileTestCardDualCheck
                code={code}
                make={make}
                model={model}
                location={loc !== '-' ? loc : undefined}
                visualResult={dualVisualResult}
                operationalResult={dualOperationalResult}
                onVisualChange={(next) => setDualCheckInput(asset.id, { visual: next })}
                onOperationalChange={(next) => setDualCheckInput(asset.id, { operational: next })}
                tested={tested}
                isSynced={dualCheckIsSynced}
                onEdit={() => openEditModal(asset, sessionEntry)}
              />
            </div>
          </>
        )}

        {isWorkshop && (
          <WorkshopCard
            asset={asset}
            sessionEntry={sessionEntry}
            onEdit={openEditModal}
            isSyncing={isSyncing}
          />
        )}

        {isStandard && (
          <>
            <div className="asset-card-desktop-only">
              <StandardCard
                asset={asset}
                sessionEntry={sessionEntry}
                onQuickPass={(a) => handleQuickPass(a, isExitEmergency ? '90-minute Discharge' : sessionTestType, sessionTestDate)}
                onFailEdit={(a) => openFailEditModal(a)}
                onEdit={openEditModal}
                isSyncing={isSyncing}
                quickPassOk={standardQuickPassOk}
                quickPassTooltip={getQuickPassTooltip(asset, sessionTestType, sessionAssetType, sessionTestType === 'Operating Time', tested)}
              />
            </div>
            <div className="asset-card-mobile-only">
              <DefaultTestCard
                equipmentCode={code}
                make={make}
                model={model}
                capacity={capacity}
                location={loc !== '-' ? loc : undefined}
                result={standardResult}
                onResultChange={(next) => {
                  if (next === 'pass' && standardQuickPassOk) {
                    handleQuickPass(asset, isExitEmergency ? '90-minute Discharge' : sessionTestType, sessionTestDate);
                  } else if (next === 'pass' || next === 'fail') {
                    openFailEditModal(asset);
                  }
                }}
                tested={tested}
                isSynced={standardIsSynced}
                onEdit={() => openEditModal(asset, sessionEntry)}
              />
            </div>
          </>
        )}
      </div>
    </li>
  );
}
